/**
 * Разбор сводки расхода, которую отдают ML и бэкенд тюнера.
 *
 * Форма одна и та же везде — прогон раннера, сабмишен лидерборда, прогон
 * оценки: набор корзин по процессам плюс корзина `total`. Раньше каждый экран
 * читал её по-своему и терял часть, поэтому разбор здесь один.
 *
 * Главная ловушка: внутри `tokens` лежит и разбивка, и уже посчитанный
 * `total_tokens`. Сложить все значения объекта — значит посчитать расход дважды.
 */

export interface UsageTokens {
  prompt_tokens?: number;
  completion_tokens?: number;
  cached_tokens?: number;
  total_tokens?: number;
  [key: string]: unknown;
}

export interface UsageBucket {
  tokens?: UsageTokens;
  total_cost?: number;
  duration_s?: number;
  extras?: Record<string, unknown>;
  agents?: Record<string, unknown>;
  [key: string]: unknown;
}

/** Процессы, которые тратят отдельно и которые надо показывать раздельно. */
export const USAGE_ROLES = ['actor', 'evaluator', 'algorithm', 'chat', 'preflight'] as const;
export type UsageRole = (typeof USAGE_ROLES)[number];

export const USAGE_ROLE_LABELS: Record<UsageRole, string> = {
  actor: 'Actor',
  evaluator: 'Evaluator',
  algorithm: 'Algorithm',
  chat: 'Chat',
  preflight: 'Preflight',
};

export interface ParsedUsageBucket {
  promptTokens: number;
  completionTokens: number;
  cachedTokens: number;
  totalTokens: number;
  totalCost: number;
  durationS: number;
  /** Прочие счётчики корзины: llm_call_count и всё, что добавится позже. */
  extras: Record<string, number>;
  /** Модель, к которой шли вызовы этой корзины. Нет — корзина без вызовов модели. */
  modelName: string | null;
  /**
   * Счётчики читаемы. `false` — они в ответе есть, но не числом: у сабмишенов,
   * записанных до 08.09, вместо них лежит `"<redacted>"`, потому что прежний
   * шаблон секретов совпадал со словом `token`. Показывать такое нулём нельзя:
   * «затёрто» и «ничего не потратил» — разные вещи, а стоимость там настоящая.
   */
  tokensKnown: boolean;
}

export interface ParsedUsageRole extends ParsedUsageBucket {
  role: string;
  label: string;
  /** Разбивка внутри роли — по агентам для актора, по подпроцессам для service. */
  parts: ParsedUsageRole[];
}

export interface ParsedUsage {
  total: ParsedUsageBucket;
  /** Хоть у одной корзины счётчики затёрты — итог по токенам неполный. */
  tokensRedacted?: boolean;
  roles: ParsedUsageRole[];
  /** Итог собран сложением ролей, потому что сервер корзину `total` не прислал. */
  totalDerived: boolean;
  llmCalls: number | null;
  unknownLlmCalls: number | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function toNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function numberOrNull(value: unknown): number | null {
  if (value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseExtras(value: unknown): Record<string, number> {
  const record = asRecord(value);
  if (!record) return {};
  const out: Record<string, number> = {};
  Object.entries(record).forEach(([key, raw]) => {
    const n = Number(raw);
    if (Number.isFinite(n)) out[key] = n;
  });
  return out;
}

/**
 * Токены одной корзины. `total_tokens` авторитетен, если сервер его прислал;
 * иначе — сумма промпта и ответа. Кешированные не прибавляются: это часть
 * промпта, а не отдельный расход.
 */
export function parseUsageBucket(value: unknown): ParsedUsageBucket | null {
  const bucket = asRecord(value);
  if (!bucket) return null;
  const tokensRecord = asRecord(bucket.tokens);
  const tokens = tokensRecord ?? {};
  const promptTokens = toNumber(tokens.prompt_tokens ?? bucket.prompt_tokens);
  const completionTokens = toNumber(tokens.completion_tokens ?? bucket.completion_tokens);
  const cachedTokens = toNumber(tokens.cached_tokens ?? bucket.cached_tokens);
  const declaredTotal = numberOrNull(tokens.total_tokens ?? bucket.total_tokens);
  // `tokens` пришло, но не объектом, и плоских счётчиков рядом тоже нет —
  // значит их затёрли, а не потратили ноль.
  const redacted = bucket.tokens != null
    && tokensRecord == null
    && declaredTotal == null
    && promptTokens === 0
    && completionTokens === 0;
  return {
    promptTokens,
    completionTokens,
    cachedTokens,
    totalTokens: declaredTotal ?? promptTokens + completionTokens,
    totalCost: toNumber(bucket.total_cost),
    durationS: toNumber(bucket.duration_s),
    extras: parseExtras(bucket.extras),
    modelName: typeof bucket.model_name === 'string' ? bucket.model_name : null,
    tokensKnown: !redacted,
  };
}

function isEmptyBucket(bucket: ParsedUsageBucket): boolean {
  if (!bucket.tokensKnown) return false;
  return (
    bucket.totalTokens === 0
    && bucket.promptTokens === 0
    && bucket.completionTokens === 0
    && bucket.cachedTokens === 0
    && bucket.totalCost === 0
    && Object.keys(bucket.extras).length === 0
  );
}

function addBuckets(target: ParsedUsageBucket, other: ParsedUsageBucket): void {
  // Неизвестное слагаемое делает неизвестной и сумму.
  if (!other.tokensKnown) target.tokensKnown = false;
  target.promptTokens += other.promptTokens;
  target.completionTokens += other.completionTokens;
  target.cachedTokens += other.cachedTokens;
  target.totalTokens += other.totalTokens;
  target.totalCost += other.totalCost;
  target.durationS += other.durationS;
}

function emptyBucket(): ParsedUsageBucket {
  return {
    promptTokens: 0,
    completionTokens: 0,
    cachedTokens: 0,
    totalTokens: 0,
    totalCost: 0,
    durationS: 0,
    extras: {},
    modelName: null,
    tokensKnown: true,
  };
}

function parseParts(bucket: Record<string, unknown>): ParsedUsageRole[] {
  const parts: ParsedUsageRole[] = [];
  const agents = asRecord(bucket.agents);
  if (agents) {
    Object.entries(agents).forEach(([name, raw]) => {
      const parsed = parseUsageBucket(raw);
      if (parsed && !isEmptyBucket(parsed)) {
        parts.push({ ...parsed, role: name, label: name, parts: [] });
      }
    });
  }
  parts.sort((a, b) => b.totalTokens - a.totalTokens || a.label.localeCompare(b.label));
  return parts;
}

/** Авторитетный статус расхода, когда бэкенд его прислал. */
export type UsageStatusHint = 'known' | 'redacted' | 'absent' | string | null | undefined;

/** Затирает `tokensKnown` рекурсивно — вместе с вложенными агентами/подпроцессами. */
function forceTokensRedacted(role: ParsedUsageRole): void {
  role.tokensKnown = false;
  role.parts.forEach(forceTokensRedacted);
}

/**
 * Разбирает сводку целиком. `null` — расхода не сообщили вовсе; это не то же
 * самое, что нули, и экран должен различать эти случаи.
 *
 * `statusHint` — авторитетный `usage_status` бэкенда, если он есть. Разбор по
 * умолчанию узнаёт «затёрто» по строке `"<redacted>"` вместо чисел — эвристика
 * бьёт по конкретному шаблону секретов и молчит, стоит ему смениться (сменили
 * маску на `"***"` — счётчики читались бы нулём, хотя расход настоящий).
 * `statusHint === 'redacted'` перекрывает эвристику явно, не гадая по строке.
 */
export function parseUsageSummary(value: unknown, statusHint?: UsageStatusHint): ParsedUsage | null {
  const summary = asRecord(value);
  if (!summary) return null;

  const roles: ParsedUsageRole[] = [];
  USAGE_ROLES.forEach((role) => {
    const raw = asRecord(summary[role]);
    if (!raw) return;
    const parsed = parseUsageBucket(raw);
    if (!parsed || isEmptyBucket(parsed)) return;
    roles.push({
      ...parsed,
      role,
      label: USAGE_ROLE_LABELS[role],
      parts: parseParts(raw),
    });
  });

  // `service` — вложенная сводка сервисных вызовов: у неё свои подпроцессы.
  const service = asRecord(summary.service);
  if (service) {
    const serviceTotal = parseUsageBucket(service.total) ?? emptyBucket();
    const serviceParts: ParsedUsageRole[] = [];
    Object.entries(service).forEach(([name, raw]) => {
      if (name === 'total') return;
      const parsed = parseUsageBucket(raw);
      if (parsed && !isEmptyBucket(parsed)) {
        serviceParts.push({ ...parsed, role: name, label: name, parts: [] });
      }
    });
    if (isEmptyBucket(serviceTotal)) {
      serviceParts.forEach((part) => addBuckets(serviceTotal, part));
    }
    if (!isEmptyBucket(serviceTotal)) {
      serviceParts.sort((a, b) => b.totalTokens - a.totalTokens || a.label.localeCompare(b.label));
      roles.push({ ...serviceTotal, role: 'service', label: 'Service', parts: serviceParts });
    }
  }

  const declaredTotal = parseUsageBucket(summary.total);
  let total = declaredTotal && !isEmptyBucket(declaredTotal) ? declaredTotal : null;
  let totalDerived = total == null;
  if (total == null && roles.length > 0) {
    total = emptyBucket();
    roles.forEach((role) => addBuckets(total as ParsedUsageBucket, role));
  }
  if (total == null) {
    // Плоская корзина без ролей и без `total` — так расход отдают прогоны
    // патча и старые ручки. Читаем сам объект как корзину, иначе разбивка
    // промпт/ответ/кеш, которая там есть, просто не доедет до экрана.
    const flat = parseUsageBucket(summary);
    if (flat && !isEmptyBucket(flat)) {
      total = flat;
      totalDerived = false;
    }
  }

  if (total == null || (isEmptyBucket(total) && roles.length === 0)) return null;

  if (statusHint === 'redacted') {
    roles.forEach(forceTokensRedacted);
    total.tokensKnown = false;
  }

  return {
    total,
    roles,
    totalDerived,
    tokensRedacted: statusHint === 'redacted' || !total.tokensKnown || roles.some((role) => !role.tokensKnown),
    llmCalls: numberOrNull(total.extras.llm_call_count),
    unknownLlmCalls: numberOrNull(total.extras.unknown_llm_call_count),
  };
}

export interface ParsedUsageCase {
  /** `f"{dataset_source}:{dataset_id}"`, генерирует бэкенд. */
  caseId: string;
  /** У кейса только actor/evaluator — расход алгоритма по датасетам не делится. */
  roles: ParsedUsageRole[];
  total: ParsedUsageBucket;
}

/**
 * Разбор `usage_by_case`: расход, поделённый по датасетам метадатасета.
 * Форма кейса та же, что и у сводки прогона, поэтому читается тем же
 * разборщиком — только `algorithm` в нём не бывает, он общий на все датасеты.
 */
export function parseUsageByCase(value: unknown): ParsedUsageCase[] {
  const record = asRecord(value);
  if (!record) return [];
  const cases: ParsedUsageCase[] = [];
  Object.entries(record).forEach(([caseId, raw]) => {
    const parsed = parseUsageSummary(raw);
    if (!parsed) return;
    cases.push({ caseId, roles: parsed.roles, total: parsed.total });
  });
  cases.sort((a, b) => a.caseId.localeCompare(b.caseId));
  return cases;
}

/**
 * Итоговые токены сводки — для узких мест вроде колонки таблицы. `null` и когда
 * расхода не сообщили, и когда счётчики затёрты: в обоих случаях числа нет, и
 * подставлять ноль значило бы соврать про бесплатный прогон.
 */
export function usageTotalTokens(value: unknown, statusHint?: UsageStatusHint): number | null {
  const parsed = parseUsageSummary(value, statusHint);
  if (!parsed || !parsed.total.tokensKnown) return null;
  return parsed.total.totalTokens;
}

/** Итоговая стоимость сводки. */
export function usageTotalCost(value: unknown, statusHint?: UsageStatusHint): number | null {
  const parsed = parseUsageSummary(value, statusHint);
  return parsed ? parsed.total.totalCost : null;
}
